// ─── Error Boundary ────────────────────────────────────────────────────────────
// Every page in this app is a `React.lazy` chunk. Without a boundary, a rejected
// chunk (or any render-time throw) propagates to the root and React unmounts the
// whole tree — the user gets a blank white screen with no way back short of a
// manual reload. This boundary catches that and renders a recoverable message
// instead, while leaving the surrounding shell (sidebar, header) mounted.
//
// A class component is the only way to catch render errors in React.

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Alert from './Alert';
import Button from './Button';
import { cn } from '../../utils/helpers';
import logger from '../../utils/logger';

/**
 * A code-split chunk that 404s — almost always because a new build was deployed
 * and the browser is still holding a cached index.html that points at the old
 * chunk filenames. Bundlers and browsers word this several different ways.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as Error).name ?? '';
  const message = (error as Error).message ?? '';
  return (
    name === 'ChunkLoadError' ||
    /loading chunk\b/i.test(message) ||
    /loading css chunk\b/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Changing this clears a caught error and re-renders `children`. Passing the
   * current route means navigating elsewhere recovers without a reload.
   */
  resetKey?: string | number;
  /** Context for the log line, e.g. "page content". */
  label?: string;
  className?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
  resetKey?: string | number;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, resetKey: undefined };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey !== state.resetKey) {
      // Route (or other reset key) changed — drop the previous failure.
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error(`Unhandled UI error${this.props.label ? ` in ${this.props.label}` : ''}`, {
      name: error.name,
      message: error.message,
      chunkLoadFailure: isChunkLoadError(error),
      componentStack: info.componentStack,
    });
  }

  handleRetry = (): void => {
    this.setState({ error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    // `React.lazy` caches a rejected import, so re-rendering the same route would
    // throw again immediately. A full reload is the only real recovery here — and
    // it is also what picks up the newly deployed build.
    const staleBuild = isChunkLoadError(error);

    return (
      <div
        className={cn(
          'flex min-h-[60vh] flex-1 items-start justify-center px-4 py-10 sm:py-16',
          this.props.className
        )}
      >
        <div className="w-full max-w-lg rounded-xl border border-borderLight bg-surface p-6 shadow-card">
          <Alert
            variant="error"
            title={staleBuild ? 'This page could not be loaded' : 'Something went wrong'}
          >
            {staleBuild
              ? 'A new version may have been deployed since you opened this tab. Reload to get the latest version of the app.'
              : 'This page stopped unexpectedly. You can try again, or use the navigation to go somewhere else.'}
          </Alert>

          {error.message && (
            <p className="mt-3 break-words font-mono text-[11px] leading-relaxed text-textSubtle">
              {error.message}
            </p>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {staleBuild ? (
              <Button variant="primary" onClick={this.handleReload}>
                Reload page
              </Button>
            ) : (
              <>
                <Button variant="primary" onClick={this.handleRetry}>
                  Try again
                </Button>
                <Button variant="outline" onClick={this.handleReload}>
                  Reload page
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}

/**
 * `ErrorBoundary` keyed on the current route, so navigating away from a broken
 * page clears the error without a reload. Must be rendered inside a Router.
 */
export const RouteErrorBoundary: React.FC<Omit<ErrorBoundaryProps, 'resetKey'>> = (props) => {
  const { pathname } = useLocation();
  return <ErrorBoundary {...props} resetKey={pathname} />;
};

export default ErrorBoundary;
